import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brandingApi, brandingQueryKeys } from "../../shared/api/branding";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** T82 — Thương hiệu. Real /api/v1/clinic/branding (logo via M11 object storage). */
export function BrandingSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: brandingQueryKeys.all(),
    queryFn: () => brandingApi.get(),
  });

  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1366d6");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) {
      setHeaderText(q.data.headerText ?? "");
      setFooterText(q.data.footerText ?? "");
      setPrimaryColor(q.data.primaryColor || "#1366d6");
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      brandingApi.update({ headerText, footerText, primaryColor }),
    onSuccess: (b) => {
      qc.setQueryData(brandingQueryKeys.all(), b);
      setMsg("Đã lưu thương hiệu.");
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const logo = useMutation({
    mutationFn: (f: File) => brandingApi.uploadLogo(f),
    onSuccess: (b) => {
      qc.setQueryData(brandingQueryKeys.all(), b);
      setMsg("Đã cập nhật logo.");
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const b = q.data;

  return (
    <div className="space-y-6 font-sans">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Thương hiệu
        </h1>
        <p className="text-sm text-muted-foreground">
          Logo phòng khám và đầu/chân trang cho các mẫu in (đơn thuốc, hoá đơn,
          biên lai).
        </p>
      </div>

      {q.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}
      {q.isError && (
        <p className="text-sm text-destructive">
          Không tải được thông tin thương hiệu.
        </p>
      )}

      {b && (
        <Card className="max-w-xl">
          <CardContent className="space-y-6 p-4 sm:p-6">
            <div className="space-y-2">
              <Label>Logo phòng khám</Label>
              <div className="flex flex-wrap items-center gap-3">
                {b.logoUrl ? (
                  <img
                    src={b.logoUrl}
                    alt="Logo"
                    className="h-16 max-w-[200px] rounded-md border bg-white object-contain p-1"
                  />
                ) : (
                  <div className="flex h-16 w-[120px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                    Chưa có logo
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  disabled={logo.isPending}
                  className="w-auto file:mr-3 file:cursor-pointer"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setMsg(null);
                      setError(null);
                      logo.mutate(f);
                    }
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {logo.isPending
                  ? "Đang tải logo…"
                  : "PNG/JPG, tối đa 200MB."}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="headerText">Đầu trang in</Label>
              <Input
                id="headerText"
                type="text"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                maxLength={512}
                placeholder="VD: PHÒNG KHÁM ĐA KHOA ADDY — 12 Nguyễn Huệ, Q.1"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="footerText">Chân trang in</Label>
              <Input
                id="footerText"
                type="text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                maxLength={512}
                placeholder="VD: Cảm ơn quý khách. Hotline: 1900 xxxx"
              />
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="primaryColor">Màu chủ đạo</Label>
              <input
                id="primaryColor"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-8 w-12 cursor-pointer rounded-md border border-input bg-background p-0"
              />
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {primaryColor}
              </code>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {msg && (
              <p className="text-sm font-medium text-emerald-600">{msg}</p>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                disabled={save.isPending}
                onClick={() => {
                  setMsg(null);
                  setError(null);
                  save.mutate();
                }}
              >
                {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
